import React, { useMemo } from 'react';
import { Marker, Polyline } from 'react-leaflet';
import { createModeIcon, statusToIconState, STATUS_COLORS } from './modeIcons';
import { modeRowKey } from '../../../utils/modeRows';
import { buildAirRoute } from '../../../utils/airRoute';


/**
 * Air and truck layers for the Global Produce Map.
 *
 * Kept separate from the sea rendering path on purpose. Sea markers are driven
 * by ShipsGo positions, voyage stops and a card-merge pipeline; air and truck
 * rows have none of that — one row is one card, and the position is computed
 * from the schedule. Sharing the sea components would mean teaching them to
 * ignore most of what they do.
 */

const num = (v) => (v == null || v === '' ? NaN : Number(v));
const latLng = (lat, lng) => {
  const a = num(lat);
  const b = num(lng);
  return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
};

export function modeRowPosition(row) {
  return latLng(row?.latitude, row?.longitude);
}

/**
 * View rows -> render items. Air position, heading and route all come from one
 * path (buildAirRoute); rows without usable endpoints keep their view position.
 * Truck is a straight origin -> destination line.
 */
export function buildModeItems(rows, mode) {
  return (rows || []).map((row) => {
    const key = modeRowKey(row);
    const origin = latLng(row?.origin_lat, row?.origin_lng);
    const dest = latLng(row?.dest_lat, row?.dest_lng);
    const state = statusToIconState(row);
    let pos = modeRowPosition(row);
    let heading = Number(row?.heading) || 0;
    let segments = [];
    let traveledSegments = [];
    let remainingSegments = [];
    if (mode === 'air') {
      const r = buildAirRoute(row);
      if (r.position) {
        pos = r.position;
        heading = r.heading;
        segments = r.segments;
        traveledSegments = r.traveledSegments;
        remainingSegments = r.remainingSegments;
      }
    } else if (origin && dest) {
      segments = [[origin, dest]];
      if (pos) {
        traveledSegments = [[origin, pos]];
        remainingSegments = [[pos, dest]];
      }
    }
    return { row, key, pos, heading, state, segments, traveledSegments, remainingSegments };
  }).filter((i) => i.pos);
}

// Route colour is the marker's colour (STATUS_COLORS by state), traveled and
// remaining alike. Traveled is solid; the remaining "way to go" is dashed and
// marches toward the destination via the shared .route-future animation.
// Arrived rows have no remaining part: the whole route is solid.

/**
 * @param {object[]} items       buildModeItems output for one mode
 * @param {string}   mode        'air' | 'truck'
 * @param {string}   selectedKey modeRowKey of the selected row
 * @param {string}   hoverKey    modeRowKey of the row under the cursor
 */
const ModeShipmentLayer = ({ items = [], mode = 'air', selectedKey = null, hoverKey = null, onSelect }) => {
  // One route at a time: the hovered row previews over the selected one.
  const activeKey = hoverKey || selectedKey;
  const active = useMemo(() => {
    const i = activeKey ? items.find((it) => it.key === activeKey) : null;
    return i && i.segments.length ? i : null;
  }, [items, activeKey]);

  const color = active ? (STATUS_COLORS[active.state] || STATUS_COLORS.on_time) : null;

  return (
    <>
      {active && active.traveledSegments.length > 0 && (
        <Polyline
          key={`route-${active.key}-traveled`}
          positions={active.traveledSegments}
          smoothFactor={1.5}
          className="route-past mode-route"
          pathOptions={{ bubblingMouseEvents: false, color, weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round' }}
        />
      )}
      {active && active.remainingSegments.length > 0 && (
        <Polyline
          key={`route-${active.key}-remaining`}
          positions={active.remainingSegments}
          smoothFactor={1.5}
          className="route-future mode-route"
          pathOptions={{ bubblingMouseEvents: false, color, weight: 4, opacity: 1, lineCap: 'round', lineJoin: 'round', dashArray: '4 10' }}
        />
      )}

      {items.map((item) => (
        <Marker
          key={`${mode}-${item.key}`}
          position={item.pos}
          icon={createModeIcon({
            mode,
            state: item.state,
            heading: item.heading,
            // Size follows the click, not the cursor: hover only draws the route.
            selected: selectedKey === item.key,
          })}
          zIndexOffset={activeKey === item.key || selectedKey === item.key ? 150000 : 60000}
          eventHandlers={{ click: () => onSelect && onSelect(item.row) }}
        />
      ))}
    </>
  );
};

export default ModeShipmentLayer;
